import {
  createSlice,
  createAsyncThunk,
  createAction,
  createDraftSafeSelector,
  isDraft,
  original,
} from '@reduxjs/toolkit';
import { format as formatDate } from 'date-fns';

import { api } from '../../api/api';
import store from '../../../store.js';
import { selectProjectUuid } from '../Project/projectSlice';
import { MAX_EXECUTION_LOG_LINES } from '../../../constants';

// actions
export const requestTaskExecute = createAction('executions/requestTaskExecute');
export const EXECUTIONS_PAGE_SIZE = 100;

// Rows come back ordered by uuid desc, and execution uuids are epoch-second
// prefixed, so the last uuid of a page is a cursor for the next one. Paging on
// that cursor costs the same at page 100 as at page 1, where `offset` makes the
// database re-read and discard every preceding row.
export const loadExecutions = createAsyncThunk(
  'executions/loadExecutions',
  async (filters, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    const cursor = store.getState().executions.cursor;
    const response = await api.executions(projectID, {
      created_start: 0,
      ...filters,
      ...(cursor ? { before_uuid: cursor } : {}),
      limit: EXECUTIONS_PAGE_SIZE,
    });
    return response;
  },
);

export const executeTask = createAsyncThunk(
  'executions/executeTask',
  async (taskID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    // if (!currentProject) return;
    thunkApi.dispatch(requestTaskExecute(taskID));
    try {
      const execution = await api.tasks.execute(projectID, taskID);
      return { execution, taskID };
    } catch (e) {
      return thunkApi.rejectWithValue({
        error: 'An error occured on the server.',
        taskID,
      });
    }
  },
);

export const killExecution = createAsyncThunk(
  'executions/killExecution',
  async (executionID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    try {
      const execution = await api.executions.kill(projectID, executionID);
      return execution;
    } catch (error) {
      return thunkApi.rejectWithValue({
        error: 'An error occured on the server.',
        executionID,
      });
    }
  },
);

export const fetchExecutionDetail = createAsyncThunk(
  'executions/fetchExecutionDetail',
  async (executionID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    try {
      const execution = await api.executions.getOne(projectID, executionID);
      return execution;
    } catch (error) {
      return thunkApi.rejectWithValue({
        error: 'An error occured on the server.',
        executionID,
      });
    }
  },
);

export const fetchExecutionStatsPlot = createAsyncThunk(
  'executions/fetchExecutionStatsPlot',
  async (executionID, thunkApi) => {
    const projectID = selectProjectUuid(thunkApi.getState());
    try {
      const statsPlot = await api.executions.statsPlot(projectID, executionID);
      if (statsPlot.error) {
        throw new Error(statsPlot.errorData.message);
      }
      return statsPlot;
    } catch (error) {
      return thunkApi.rejectWithValue({
        error,
      });
    }
  },
);

// Keyed by execution uuid: { source, opened, buffer, timer, flush }.
// `opened` distinguishes the first connect from the polyfill's automatic
// reconnects; `buffer` holds formatted lines awaiting the next batched flush.
const activeLogSources = {};

// A finished run replays up to MAX_EXECUTION_LOG_LINES frames back-to-back.
// Dispatching (and copying the buffer, and re-rendering) once per frame froze
// the tab, so frames are batched and flushed at most once per interval.
export const LOG_FLUSH_INTERVAL_MS = 100;

export const closeExecutionLogStream = (executionID) => {
  const entry = activeLogSources[executionID];
  if (entry) {
    // Deliver anything still buffered before tearing the source down.
    entry.flush();
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.source.close();
    delete activeLogSources[executionID];
  }
};

// Log-stream status actions (dispatched from the EventSource callbacks).
export const executionLogStreamOpened = createAction(
  'executions/executionLogStreamOpened',
);
export const executionLogStreamFailed = createAction(
  'executions/executionLogStreamFailed',
);
export const executionLogStreamCompleted = createAction(
  'executions/executionLogStreamCompleted',
);
// The server sends `{"truncated": true}` ahead of the replay when a finished
// log was longer than it will replay; only the newest lines follow.
export const executionLogStreamTruncated = createAction(
  'executions/executionLogStreamTruncated',
);
// Batched append: `lines` are formatted entries in arrival (oldest-first)
// order.
export const appendExecutionLogLines = createAction(
  'executions/appendExecutionLogLines',
);
// Synchronous so it can never land after (and wipe) frames handled right
// after `onopen`.
export const resetExecutionLogStream = createAction(
  'executions/resetExecutionLogStream',
);

// Monotonic counter for React keys: a frame may arrive without a usable
// timestamp, and `Math.random() + undefined` is NaN.
let logLineSequence = 0;

const formatLogTimestamp = (timestamp) => {
  const parsed = new Date(timestamp);
  if (timestamp !== undefined && timestamp !== null && !isNaN(parsed)) {
    return formatDate(parsed, 'yyyy/MM/dd HH:mm:ss');
  }
  // Never throw on a malformed frame: render whatever the server sent, or fall
  // back to now, so the line still reaches the console.
  if (timestamp) return String(timestamp);
  return formatDate(new Date(), 'yyyy/MM/dd HH:mm:ss');
};

export const formatExecutionLogLine = (executionID, log) => {
  const { levelname, timestamp, name, message } = log;
  const timestampFormat = formatLogTimestamp(timestamp);
  logLineSequence += 1;
  const id = `${executionID}-${logLineSequence}`;
  let line = '[' + levelname + ']';
  if (levelname === 'INFO') line += '   ';
  line += `     ${timestampFormat}     ${name}   ${message}`;

  return {
    line,
    _id: id,
    level: levelname,
    data: log,
  };
};

export const startExecutionLogStream = createAsyncThunk(
  'status/startExecutionLogStream',
  async (executionID, thunkAPI) => {
    closeExecutionLogStream(executionID);
    const projectID = selectProjectUuid(thunkAPI.getState());
    const logSource = api.executions.logSource(projectID, executionID);
    const entry = { source: logSource, opened: false, buffer: [], timer: null };
    entry.flush = () => {
      if (entry.buffer.length === 0) return;
      const lines = entry.buffer;
      entry.buffer = [];
      thunkAPI.dispatch(appendExecutionLogLines({ executionID, lines }));
    };
    // Leading-edge throttle: an idle stream's first frame shows immediately
    // (live tail stays responsive), then frames in a burst are batched until
    // a tick finds nothing new.
    const tick = () => {
      entry.timer = null;
      if (entry.buffer.length === 0) return;
      entry.flush();
      entry.timer = setTimeout(tick, LOG_FLUSH_INTERVAL_MS);
    };
    activeLogSources[executionID] = entry;
    logSource.onopen = () => {
      const firstOpen = !entry.opened;
      entry.opened = true;
      thunkAPI.dispatch(executionLogStreamOpened(executionID));
      // The eventsource polyfill reconnects automatically when the server
      // closes the stream. Resetting on a reconnect would wipe every line we
      // already received, so only the first open clears the buffer.
      if (firstOpen) {
        thunkAPI.dispatch(resetExecutionLogStream(executionID));
      }
    };
    logSource.onerror = () => {
      // Ignore errors from a source we deliberately closed (remount, or the
      // server signalled `complete`).
      if (activeLogSources[executionID] !== entry) return;
      entry.flush();
      thunkAPI.dispatch(
        executionLogStreamFailed({
          executionID,
          error: 'Could not connect to the log stream.',
        }),
      );
    };
    logSource.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.complete) {
        // closeExecutionLogStream flushes the buffer first.
        closeExecutionLogStream(executionID);
        thunkAPI.dispatch(executionLogStreamCompleted(executionID));
      } else if (data.truncated) {
        entry.flush();
        thunkAPI.dispatch(executionLogStreamTruncated(executionID));
      } else {
        entry.buffer.push(formatExecutionLogLine(executionID, data));
        if (!entry.timer) {
          entry.flush();
          entry.timer = setTimeout(tick, LOG_FLUSH_INTERVAL_MS);
        }
      }
    };
  },
);

// Single-frame append, kept for callers outside the stream; the stream itself
// batches through `appendExecutionLogLines`.
export const updateExecutionLogStream = createAsyncThunk(
  'status/updateExecutionLogStream',
  async ({ executionID, data: log }) => {
    return {
      executionID,
      data: formatExecutionLogLine(executionID, log),
    };
  },
);

// Prepend `lines` (oldest-first) newest-first, cap the buffer, and count what
// the cap evicted.
const appendLines = (state, executionID, lines) => {
  if (!lines || lines.length === 0) return;
  // A frame can land before start/reset has fulfilled - default the buffer
  // rather than throwing inside the reducer and dropping the line.
  const current = state.executionLogs[executionID] || [];
  // Read the un-drafted array so Immer doesn't proxy up to 5000 entries just
  // to copy them.
  const existing = isDraft(current) ? original(current) : current;
  const combined = lines.slice().reverse().concat(existing);
  const dropped = combined.length - MAX_EXECUTION_LOG_LINES;
  if (dropped > 0) {
    combined.length = MAX_EXECUTION_LOG_LINES;
    if (!state.executionLogsDropped) state.executionLogsDropped = {};
    state.executionLogsDropped[executionID] =
      (state.executionLogsDropped[executionID] || 0) + dropped;
  }
  state.executionLogs[executionID] = combined;
  const status = state.executionLogsStatus[executionID];
  if (status) {
    status.loading = false;
  } else {
    state.executionLogsStatus[executionID] = {
      loading: false,
      error: null,
    };
  }
};

// selectors
const selectSelf = (state) => state.executions;
export const selectExecutions = createDraftSafeSelector(
  selectSelf,
  ({ executions }) => executions,
);
export const selectMode = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.mode,
);
export const selectExecutionsLoading = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.loading,
);
export const selectExecutionDetail = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.executionDetail,
);
export const selectExecutionStatsPlot = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.statsPlot,
);
export const selectExecutionLogs = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.executionLogs,
);
export const selectExecutionLogsStatus = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.executionLogsStatus,
);
// Per-execution count of lines evicted from the buffer by the cap.
export const selectExecutionLogsDropped = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.executionLogsDropped || {},
);
// Per-execution flag: the server replayed only the newest lines.
export const selectExecutionLogsTruncated = createDraftSafeSelector(
  selectSelf,
  (executions) => executions.executionLogsTruncated || {},
);

const executionSlice = createSlice({
  name: 'executions',
  initialState: {
    executions: [],
    moreAvailable: true,
    loading: false,
    execution: null,
    executionError: null,
    requestingExecutions: {},
    executionDetail: {
      execution: null,
      loading: false,
      error: null,
      // killing
      killing: false,
      killingError: null,
    },
    cursor: null,
    mode: 'all',
    statsPlot: {
      error: null,
      plot: null,
      isFetching: false,
    },
    executionLogs: {},
    executionLogsStatus: {},
    executionLogsDropped: {},
    executionLogsTruncated: {},
  },
  reducers: {
    setMode(state, action) {
      state.mode = action.payload;
    },
    deleteExecutions(state, action) {
      state.cursor = null;
      state.executions = [];
      state.moreAvailable = true;
    },
  },
  extraReducers: {
    [requestTaskExecute.type]: (state, action) => {
      state.requestingExecutions[action.payload] = true;
    },
    [executeTask.fulfilled]: (state, action) => {
      const { execution, taskID } = action.payload;
      state.execution = execution;
      state.requestingExecutions[taskID] = false;
    },
    [executeTask.rejected]: (state, action) => {
      const { error, taskID } = action.payload;
      state.executionError = error;
      state.requestingExecutions[taskID] = false;
    },
    [killExecution.pending]: (state, _action) => {
      state.executionDetail.killing = true;
      state.executionDetail.killingError = null;
    },
    [killExecution.fulfilled]: (state, action) => {
      const execution = action.payload;
      state.executionDetail.killing = false;
      state.executionDetail.killingError = null;
      if (execution?.uuid) {
        const idx = state.executions.findIndex(
          (e) => e.uuid === execution.uuid,
        );
        if (idx !== -1) {
          state.executions[idx] = execution;
        }
        if (state.executionDetail.execution?.uuid === execution.uuid) {
          state.executionDetail.execution = execution;
        }
      }
    },
    [killExecution.rejected]: (state, action) => {
      const { error } = action.payload;
      state.executionDetail.killing = false;
      state.executionDetail.killingError = error;
    },
    [fetchExecutionDetail.pending]: (state, _action) => {
      state.executionDetail.loading = true;
      state.executionDetail.error = null;
    },
    [fetchExecutionDetail.fulfilled]: (state, action) => {
      const execution = action.payload;
      state.executionDetail.execution = execution;
      state.executionDetail.loading = false;
      state.executionDetail.error = null;
      const idx = state.executions.findIndex((e) => e.uuid === execution.uuid);
      if (idx !== -1) {
        state.executions[idx] = execution;
      }
    },
    [fetchExecutionDetail.rejected]: (state, action) => {
      const { error } = action.payload;
      state.executionDetail.execution = null;
      state.executionDetail.loading = false;
      state.executionDetail.error = error;
    },
    [loadExecutions.pending]: (state, action) => {
      state.loading = true;
    },
    [loadExecutions.fulfilled]: (state, action) => {
      state.executions = state.executions.concat(action.payload);
      state.loading = false;
      if (action.payload.length > 0) {
        state.cursor = action.payload[action.payload.length - 1].uuid;
      }
      if (action.payload.length < EXECUTIONS_PAGE_SIZE) {
        state.moreAvailable = false;
      }
    },
    [fetchExecutionStatsPlot.pending]: (state) => {
      state.statsPlot = {
        error: null,
        plot: null,
        isFetching: true,
      };
    },
    [fetchExecutionStatsPlot.fulfilled]: (state, action) => {
      state.statsPlot = {
        error: null,
        plot: action.payload,
        isFetching: false,
      };
    },
    [fetchExecutionStatsPlot.rejected]: (state, action) => {
      state.statsPlot = {
        error: action.payload.error,
        plot: null,
        isFetching: false,
      };
    },
    // Reset only clears lines and the client-side eviction count. The
    // server's `truncated` flag is cleared when a stream starts (pending), so
    // a reset on first open can never wipe it.
    [resetExecutionLogStream.type]: (state, action) => {
      state.executionLogs[action.payload] = [];
      if (state.executionLogsDropped) {
        delete state.executionLogsDropped[action.payload];
      }
    },
    [startExecutionLogStream.pending]: (state, action) => {
      if (state.executionLogsTruncated) {
        delete state.executionLogsTruncated[action.meta.arg];
      }
      state.executionLogsStatus[action.meta.arg] = {
        loading: true,
        error: null,
      };
    },
    [startExecutionLogStream.fulfilled]: (state, action) => {
      state.executionLogs[action.meta.arg] = [];
      if (state.executionLogsDropped) {
        delete state.executionLogsDropped[action.meta.arg];
      }
      state.executionLogsStatus[action.meta.arg] = {
        loading: true,
        error: null,
      };
    },
    [startExecutionLogStream.rejected]: (state, action) => {
      state.executionLogsStatus[action.meta.arg] = {
        loading: false,
        error: 'Could not open the log stream.',
      };
    },
    [executionLogStreamOpened.type]: (state, action) => {
      state.executionLogsStatus[action.payload] = {
        loading: false,
        error: null,
      };
    },
    [executionLogStreamCompleted.type]: (state, action) => {
      state.executionLogsStatus[action.payload] = {
        loading: false,
        error: null,
      };
    },
    [executionLogStreamFailed.type]: (state, action) => {
      const { executionID, error } = action.payload;
      state.executionLogsStatus[executionID] = {
        loading: false,
        error: error || 'Could not connect to the log stream.',
      };
    },
    [executionLogStreamTruncated.type]: (state, action) => {
      if (!state.executionLogsTruncated) state.executionLogsTruncated = {};
      state.executionLogsTruncated[action.payload] = true;
    },
    [appendExecutionLogLines.type]: (state, action) => {
      const { executionID, lines } = action.payload;
      appendLines(state, executionID, lines);
    },
    [updateExecutionLogStream.fulfilled]: (state, action) => {
      const { executionID, data } = action.payload;
      appendLines(state, executionID, [data]);
    },
  },
});

export const { setMode, deleteExecutions } = executionSlice.actions;

export default executionSlice.reducer;
